import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { env } from "@/config/env";
import { useAuthStore } from "@/stores/authStore";
import { useChatStore } from "@/stores/chatStore";
import { useMapStore } from "@/stores/mapStore";
import { useMarketStore } from "@/stores/marketStore";
import { WebSocketService } from "@/services/websocket";
import { listConversations, getConversation, deleteConversation, apiMessageToChatMessage } from "@/services/api";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { useToastStore } from "@/stores/toastStore";
import { useUsageStore } from "@/stores/usageStore";
import { ChatPanel } from "@/features/chat/components/ChatPanel";
import { MapPanel } from "@/features/map/components/MapPanel";
import { Sidebar } from "@/app/Sidebar";
import { ResizablePanels } from "@/app/ResizablePanels";
import { CommandPalette } from "@/features/command-palette/CommandPalette";
import { ShortcutsDialog } from "@/features/shortcuts/ShortcutsDialog";
import { SettingsDialog } from "@/features/settings/SettingsDialog";
import { MobileLayout } from "@/app/MobileLayout";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { FocusTrap } from "@/components/a11y/FocusTrap";
import type { ServerMessage } from "@/types";
import { extractMapContext } from "@/features/map/utils/mapContext";
import type { Map as MapboxMap } from "mapbox-gl";
import { DevTierPicker, getDevTierParams } from "@/features/dev/DevTierPicker";

export function MainLayout() {
  const { t } = useTranslation();
  const breakpoint = useBreakpoint();
  const wsRef = useRef<WebSocketService | null>(null);
  const cancelledRef = useRef(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [devTierKey, setDevTierKey] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window !== "undefined" && window.innerWidth < 1024) return true;
    const stored = localStorage.getItem("ortvis-sidebar-collapsed");
    return stored === null ? true : stored === "true";
  });

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("ortvis-sidebar-collapsed", String(next));
      return next;
    });
  }, []);

  const { getIdToken } = useAuthStore();
  const {
    addMessage,
    appendToLastMessage,
    setIsStreaming,
    setConversationId,
    addActiveTool,
    completeActiveTool,
    clearActiveTools,
    updateLastMessageMapFeatures,
    setConversations,
    updateConversationTitle,
    loadConversation,
  } = useChatStore();
  const { setPendingFeatures, setPendingViewport, commitPendingFeatures, clearPending, clearFeatures, setFeatures } = useMapStore();
  const { market } = useMarketStore();

  const refreshConversations = useCallback(async () => {
    try {
      const convs = await listConversations();
      setConversations(convs);
    } catch {
      // Silent — sidebar stays as-is
    }
  }, [setConversations]);

  const handleServerMessage = useCallback(
    (message: ServerMessage) => {
      // After user cancels streaming, ignore content messages until turn completes
      if (cancelledRef.current) {
        if (message.type === "turn_complete") {
          cancelledRef.current = false;
          setConversationId(message.conversation_id);
          if (message.title) {
            updateConversationTitle(message.conversation_id, message.title);
          }
          refreshConversations();
        }
        return;
      }

      switch (message.type) {
        case "text_delta":
          appendToLastMessage(message.content);
          break;
        case "thinking_status":
          useChatStore.getState().addThinkingStep(message.text);
          break;
        case "map_intent":
          useMapStore.getState().resetForNewTurn();
          useMapStore.getState().setMapIntent({ mode: message.mode, transition: message.transition });
          if (message.transition === "reset") {
            // Clear existing entities with animation
            setPendingFeatures({ type: "FeatureCollection", features: [] });
            commitPendingFeatures();
          }
          break;
        case "map_update": {
          const intent = useMapStore.getState().mapIntent;
          if (intent?.mode === "analysis") {
            // Stage features invisibly — don't commit to map yet
            useMapStore.getState().stageFeatures(message.features);
            updateLastMessageMapFeatures(message.features);
            if (message.viewport) setPendingViewport(message.viewport);
          } else {
            // Lookup mode (or no intent) — immediate rendering (current behavior)
            setPendingFeatures(message.features);
            updateLastMessageMapFeatures(message.features);
            if (message.viewport) setPendingViewport(message.viewport);
            commitPendingFeatures();
          }
          break;
        }
        case "tool_start":
          addActiveTool(message.tool_name, message.context);
          break;
        case "tool_complete":
          completeActiveTool(message.tool_name, message.summary);
          break;
        case "turn_complete": {
          const store = useMapStore.getState();
          if (store.mapIntent?.mode === "analysis") {
            store.commitRevealedFeatures();
          }
          useChatStore.getState().persistThinkingSteps();
          useChatStore.getState().clearThinkingSteps();
          setIsStreaming(false);
          clearActiveTools();

          setConversationId(message.conversation_id);
          commitPendingFeatures();
          if (message.title) {
            updateConversationTitle(message.conversation_id, message.title);
          }
          refreshConversations();
          break;
        }
        case "error":
          useChatStore.getState().clearThinkingSteps();
          setIsStreaming(false);
          clearActiveTools();

          console.error("[Ortvis] Server error:", message.message);
          if (message.code === "LIMIT_REACHED") {
            useChatStore.getState().setLastMessageType("limit_reached");
          } else {
            appendToLastMessage(`\n\n*${t("chat.errorMessage")}*`);
          }
          break;
        case "usage_update":
          useUsageStore.getState().updateUsage(message);
          break;
        case "property_block":
          useChatStore.getState().setLastMessagePropertyBlock(message.data);
          break;
        case "company_block":
          useChatStore.getState().setLastMessageCompanyBlock(message.data);
          break;
        case "municipality_block":
          useChatStore.getState().setLastMessageMunicipalityBlock(message.data);
          break;
        case "context_request": {
          // Backend tool requesting map context from Streets v8 tiles
          const rawMap = useMapStore.getState().mapInstance as MapboxMap | null;
          let ctx: Record<string, unknown> | null = null;
          if (rawMap) {
            const loc = useMapStore.getState().clickedLocation;
            const center: [number, number] = message.center
              ?? (loc ? [loc.longitude, loc.latitude] : [rawMap.getCenter().lng, rawMap.getCenter().lat]);
            ctx = extractMapContext(rawMap, center, message.radius_px ?? 100, message.include) as Record<string, unknown> | null;
          }
          wsRef.current?.send({
            action: "context_response",
            request_id: message.request_id,
            context: ctx,
          });
          break;
        }
      }
    },
    [
      appendToLastMessage,
      setPendingFeatures,
      setPendingViewport,
      commitPendingFeatures,
      updateLastMessageMapFeatures,
      addActiveTool,
      completeActiveTool,
      clearActiveTools,
      setIsStreaming,
      setConversationId,
      updateConversationTitle,
      refreshConversations,
      t,
    ],
  );

  useEffect(() => {
    let ws: WebSocketService | null = null;

    async function connect() {
      if (env.isDev) {
        const { tier, maxTier } = getDevTierParams();
        const params = new URLSearchParams({ tier });
        if (maxTier) params.set("max_tier", maxTier);
        const wsUrl = `ws://${window.location.hostname}:8000/ws?${params}`;
        ws = new WebSocketService(wsUrl, () => Promise.resolve(""));
      } else {
        ws = new WebSocketService(env.aws.websocketUrl, () => getIdToken().then((t) => t ?? ""));
      }

      wsRef.current = ws;
      ws.onMessage(handleServerMessage);
      let hadConnection = false;
      ws.onStatus((connected) => {
        const wasConnected = hadConnection;
        setWsConnected(connected);
        if (!connected) {
          // Clear pending map features on any disconnect
          useMapStore.getState().clearPending();

          if (wasConnected) {
            useToastStore.getState().addToast({
              type: "error",
              message: t("toast.disconnected"),
              duration: 8000,
            });
          }

          if (useChatStore.getState().isStreaming) {
            // Was streaming when connection dropped
            useChatStore.getState().setIsStreaming(false);
            useChatStore.getState().failActiveTools();
            useChatStore.getState().clearActiveTools();
            useChatStore.getState().setDisconnectedWhileStreaming(true);
            useChatStore.getState().appendToLastMessage(
              `\n\n*${t("toast.streamingInterrupted")}*`
            );
          }
        } else {
          if (wasConnected === false && hadConnection) {
            // Reconnected after a disconnect
            useToastStore.getState().addToast({
              type: "success",
              message: t("toast.reconnected"),
              duration: 3000,
            });
          }
        }
        hadConnection = connected;
      });

      try {
        await ws.connect();
      } catch {
        // Will auto-reconnect
      }
    }

    connect();

    return () => {
      ws?.disconnect();
      wsRef.current = null;
    };
  }, [getIdToken, handleServerMessage, devTierKey]);

  // Load conversation list on mount
  useEffect(() => {
    refreshConversations();
  }, [refreshConversations]);

  // ── Keyboard shortcuts ──
  const handleNewChat = useCallback(() => {
    useChatStore.getState().resetChat();
    useMapStore.getState().clearFeatures();
  }, []);

  const [paletteOpen, setPaletteOpen] = useState(false);
  const togglePalette = useCallback(() => {
    setPaletteOpen((v) => !v);
  }, []);

  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const toggleShortcuts = useCallback(() => {
    setShortcutsOpen((v) => !v);
  }, []);

  const handleStopStreaming = useCallback(() => {
    if (!useChatStore.getState().isStreaming) return;
    cancelledRef.current = true;
    setIsStreaming(false);
    clearActiveTools();
    useMapStore.getState().clearPending();
  }, [setIsStreaming, clearActiveTools]);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const toggleSettingsDialog = useCallback(() => {
    setSettingsOpen((v) => !v);
  }, []);

  useKeyboardShortcuts([
    { id: "toggle-sidebar", keys: ["mod+b"], action: toggleSidebar, global: true },
    { id: "new-chat", keys: ["mod+shift+n"], action: handleNewChat, global: true },
    { id: "command-palette", keys: ["mod+k"], action: togglePalette, global: true },
    { id: "stop-streaming", keys: ["escape"], action: handleStopStreaming, global: true },
    { id: "shortcuts-help", keys: ["?"], action: toggleShortcuts },
    { id: "settings", keys: ["mod+,"], action: toggleSettingsDialog, global: true },
  ]);

  const handleSendMessage = useCallback(
    (content: string) => {
      const ws = wsRef.current;
      if (!ws?.isConnected) return;

      const store = useMapStore.getState();
      const conversationId = useChatStore.getState().conversationId;
      const location = store.clickedLocation;
      const polygon = store.drawnPolygon;

      // Reset cancel state and store for retry on disconnect
      cancelledRef.current = false;
      useChatStore.getState().setLastUserMessage(content);
      useChatStore.getState().setDisconnectedWhileStreaming(false);

      // Reset interaction state + clear any pending map data from previous turn
      store.setUserHasInteracted(false);
      clearPending();

      // Build display content (show location/polygon name in the user message bubble)
      const contextName = location?.name ?? polygon?.name;
      const displayContent = contextName && !content
        ? t("chat.tellMeAbout", { name: contextName })
        : content;

      addMessage({
        id: crypto.randomUUID(),
        role: "user",
        content: displayContent || content,
        createdAt: new Date().toISOString(),
      });

      addMessage({
        id: crypto.randomUUID(),
        role: "assistant",
        content: "",
        createdAt: new Date().toISOString(),
      });

      setIsStreaming(true);

      // Build drawn polygon GeoJSON geometry (close the ring)
      const drawnPolygonGeom = polygon && polygon.coordinates.length >= 3
        ? { type: "Polygon" as const, coordinates: [[...polygon.coordinates, polygon.coordinates[0]!]] }
        : undefined;

      ws.send({
        action: "message",
        content: content || (location ? t("chat.tellMeAboutArea") : polygon ? t("chat.tellMeAboutArea") : ""),
        conversation_id: conversationId ?? undefined,
        market,
        location: location
          ? { latitude: location.latitude, longitude: location.longitude, name: location.name }
          : undefined,
        drawn_polygon: drawnPolygonGeom,
      });

      // Location persists across conversation (same as polygon) — user clears with X
    },
    [addMessage, setIsStreaming, clearPending, market, t],
  );

  const handleLoadConversation = useCallback(
    async (conversationId: string) => {
      const { messages } = await getConversation(conversationId);
      const chatMessages = messages.map(apiMessageToChatMessage);
      loadConversation(conversationId, chatMessages);
      clearFeatures();

      // Restore map from the last message that had features
      for (let i = chatMessages.length - 1; i >= 0; i--) {
        const features = chatMessages[i]?.mapFeatures;
        if (features) {
          setFeatures(features);
          break;
        }
      }
    },
    [loadConversation, clearFeatures, setFeatures],
  );

  const handleDeleteConversation = useCallback(
    async (conversationId: string) => {
      try {
        await deleteConversation(conversationId);
        // If deleting the active conversation, reset to new chat
        if (useChatStore.getState().conversationId === conversationId) {
          useChatStore.getState().resetChat();
          clearFeatures();
        }
        refreshConversations();
      } catch {
        // Silent failure
      }
    },
    [clearFeatures, refreshConversations],
  );

  const handleAskAbout = useCallback(
    (entityType: string, _entityId: string, name: string) => {
      const store = useMapStore.getState();
      store.setPopupInfo(null);
      // Keep current map view — user is already looking at this area
      store.setUserHasInteracted(true);
      const promptKeys: Record<string, string> = {
        property: "chat.tellMeAbout",
        project: "chat.tellMeAboutProject",
        permit: "chat.tellMeAboutPermit",
        tender: "chat.tellMeAboutTender",
      };
      const prompt = t(promptKeys[entityType] ?? "chat.tellMeAbout", { name });
      handleSendMessage(prompt);
    },
    [handleSendMessage, t],
  );

  const handleEntityClick = useCallback(
    (entityType: string, entityId: string, _name: string) => {
      const store = useMapStore.getState();
      // Fly to the entity on the map if it exists — never auto-send messages
      store.setFlyToTarget({ entityId, entityType, timestamp: Date.now() });
    },
    [],
  );

  const panelDefaults = breakpoint === "tablet"
    ? { defaultLeftPercent: 45, minLeftPx: 280, minRightPx: 260 }
    : { defaultLeftPercent: 40, minLeftPx: 320, minRightPx: 300 };

  const sidebarIsOverlay = breakpoint === "tablet";

  return (
    <>
      {breakpoint === "phone" ? (
        <MobileLayout
          onSendMessage={handleSendMessage}
          onEntityClick={handleEntityClick}
          onStopStreaming={handleStopStreaming}
          onOpenSettings={() => setSettingsOpen(true)}
          onLoadConversation={handleLoadConversation}
          onDeleteConversation={handleDeleteConversation}
          onAskAbout={handleAskAbout}
          disabled={!wsConnected}
        />
      ) : (
        <div className="flex h-full relative">
          <a href="#chat-input" className="cx-skip-link">Skip to chat</a>

          {sidebarIsOverlay ? (
            <>
              {!sidebarCollapsed && (
                <div
                  className="cx-mobile-drawer-backdrop animate-cx-fade-in"
                  onClick={toggleSidebar}
                />
              )}
              <div className={`cx-mobile-drawer ${!sidebarCollapsed ? "cx-mobile-drawer--open" : ""}`}>
                <FocusTrap>
                  <Sidebar
                    isCollapsed={false}
                    onToggle={toggleSidebar}
                    onLoadConversation={handleLoadConversation}
                    onDeleteConversation={handleDeleteConversation}
                    onOpenSettings={() => setSettingsOpen(true)}
                  />
                </FocusTrap>
              </div>
            </>
          ) : (
            <Sidebar
              isCollapsed={sidebarCollapsed}
              onToggle={toggleSidebar}
              onLoadConversation={handleLoadConversation}
              onDeleteConversation={handleDeleteConversation}
              onOpenSettings={() => setSettingsOpen(true)}
            />
          )}

          <main className="flex-1 min-w-0 flex flex-col" aria-label="Chat">
            <div className="flex-1 min-h-0">
              <ResizablePanels
                left={<ChatPanel onSendMessage={handleSendMessage} onEntityClick={handleEntityClick} onStopStreaming={handleStopStreaming} disabled={!wsConnected} />}
                right={<MapPanel onAskAbout={handleAskAbout} />}
                defaultLeftPercent={panelDefaults.defaultLeftPercent}
                minLeftPx={panelDefaults.minLeftPx}
                minRightPx={panelDefaults.minRightPx}
              />
            </div>
          </main>
        </div>
      )}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onNewChat={handleNewChat}
        onToggleSidebar={toggleSidebar}
        onLoadConversation={handleLoadConversation}
      />
      <ShortcutsDialog
        open={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
      />
      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
      {env.isDev && (
        <DevTierPicker
          onTierChange={() => {
            wsRef.current?.disconnect();
            useUsageStore.getState().reset();
            setDevTierKey((k) => k + 1);
          }}
        />
      )}
    </>
  );
}
